// notificationService.js - Service for creating notifications with proper templates

import { supabase } from '../data/supabaseClient';

/**
 * Creates a notification with template support for multi-language
 * @param {Object} params - Notification parameters
 * @returns {Promise} Result of notification creation
 */
export const createTemplatedNotification = async ({
  targetUserId,
  type,
  category,
  actorId = null,
  relatedId = null,
  actionUrl = null,
  priority = 'normal',
  templateKey,
  templateParams = {},
  fallbackMessage = '' // Fallback for older clients
}) => {
  try {
    // Call the updated create_notification function with template data
    const { data, error } = await supabase.rpc('create_notification', {
      target_user_id: targetUserId,
      notification_type: type,
      notification_category: category,
      notification_message: fallbackMessage, // Fallback for backward compatibility
      notification_data: {},
      notification_actor_id: actorId,
      notification_related_id: relatedId,
      notification_action_url: actionUrl,
      notification_priority: priority,
      notification_template: templateKey,
      notification_params: templateParams
    });
    
    if (error) throw error;
    return { success: true, notificationId: data };
  } catch (error) {
    console.error('Error creating notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send a new message notification
 */
export const sendNewMessageNotification = async (conversationId, senderId, recipientId, senderName, messagePreview) => {
  return createTemplatedNotification({
    targetUserId: recipientId,
    type: 'new_message',
    category: 'messages',
    actorId: senderId,
    relatedId: conversationId,
    actionUrl: '/chat',
    priority: 'normal',
    templateKey: 'notification.new_message',
    templateParams: {
      sender_name: senderName,
      message_preview: messagePreview?.substring(0, 50)
    },
    fallbackMessage: `${senderName} sent you a message`
  });
};

/**
 * Send a new conversation notification
 */
export const sendNewConversationNotification = async (conversationId, senderId, recipientId, senderName) => {
  return createTemplatedNotification({
    targetUserId: recipientId,
    type: 'new_conversation',
    category: 'messages',
    actorId: senderId,
    relatedId: conversationId,
    actionUrl: '/chat',
    priority: 'normal',
    templateKey: 'notification.new_conversation',
    templateParams: {
      sender_name: senderName
    },
    fallbackMessage: `${senderName} started a conversation with you`
  });
};

/**
 * Send a ride request notification
 */
export const sendRideRequestNotification = async (rideId, requesterId, driverId, requesterName) => {
  return createTemplatedNotification({
    targetUserId: driverId,
    type: 'ride_request',
    category: 'rides',
    actorId: requesterId,
    relatedId: rideId,
    actionUrl: '/rides',
    priority: 'high',
    templateKey: 'notification.ride_request',
    templateParams: {
      requester_name: requesterName
    },
    fallbackMessage: `${requesterName} requested to join your ride`
  });
};

/**
 * Send a package request notification
 */
export const sendPackageRequestNotification = async (packageId, senderId, carrierId, senderName) => {
  return createTemplatedNotification({
    targetUserId: carrierId,
    type: 'package_request',
    category: 'packages',
    actorId: senderId,
    relatedId: packageId,
    actionUrl: '/packages',
    priority: 'high',
    templateKey: 'notification.package_request',
    templateParams: {
      sender_name: senderName
    },
    fallbackMessage: `${senderName} wants to send a package on your route`
  });
};

/**
 * Send a review notification
 */
export const sendReviewNotification = async (reviewId, reviewerId, revieweeId, reviewerName, rating) => {
  return createTemplatedNotification({
    targetUserId: revieweeId,
    type: 'new_review',
    category: 'general',
    actorId: reviewerId,
    relatedId: reviewId,
    actionUrl: '/profile',
    priority: 'normal',
    templateKey: 'notification.new_review',
    templateParams: {
      reviewer_name: reviewerName,
      rating: rating
    },
    fallbackMessage: `${reviewerName} left you a ${rating}-star review!`
  });
};

export default {
  createTemplatedNotification,
  sendNewMessageNotification,
  sendNewConversationNotification,
  sendRideRequestNotification,
  sendPackageRequestNotification,
  sendReviewNotification
};
